import {
  Box,
  Typography,
  Avatar,
} from '@mui/material';
import { T, Icon, stitchPill, stitchCard } from './stitch';
import { formatDate, getInitials, stringToColor } from './projectMeta';
import EindeDraaidagRapport from './EindeDraaidagRapport';
import { crewSamengevoegd } from '../../../utils/draaidagCrew';
import KlantVereistenBlok from '../../../ui-components/KlantVereistenBlok';


function DraaidagCard({ draaidag, rapporten = [] }) {
  const name = draaidag.fields?.Draaidag || 'Draaidag';
  const startDate = draaidag.fields?.StartdatumOutlook ? formatDate(draaidag.fields.StartdatumOutlook) : null;
  const planningstatus = draaidag.fields?.Planningstatus || '';
  const pl = draaidag.fields?.PL || '';
  const geenMontage = draaidag.fields?.['Geen montage nodig'];

  // Use the lookup field for crew names (much more reliable!)
  const crewLookup = draaidag.fields?.['Naam (from Crew)'] || [];
  // + namen uit de Outlook-titel die niet gekoppeld zijn (externe crew "(b)")
  const crew = crewSamengevoegd(crewLookup, draaidag.fields?.Draaidag).alle;

  // Callsheet & Quitclaim status
  const callsheetNodig = draaidag.fields?.['Callsheet nodig?'] || '';
  const aantalCallsheets = draaidag.fields?.['Aantal callsheets'] || 0;
  const quitclaimsNodig = draaidag.fields?.['Quitclaims nodig?'] || '';
  const quitclaims = draaidag.fields?.Quitclaims || [];

  const statusColor = planningstatus.includes('ingepland') ? T.emerald :
                      planningstatus.includes('mogelijk') ? T.amber : T.outline;

  return (
    <Box sx={{ p: 2.5, ...stitchCard, borderLeft: `4px solid ${T.emerald}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontFamily: T.font, fontWeight: 700 }}>{name}</Typography>
          {startDate && (
            <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Icon name="calendar_today" size={14} />
              {startDate}
            </Typography>
          )}
        </Box>
        {planningstatus && (
          <Typography component="span" sx={{ ...stitchPill('#fff', statusColor), display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Icon name="event" size={12} />
            {planningstatus}
          </Typography>
        )}
      </Box>

      {/* Klantvereisten (één regel; ontbrekende geheimhouding voor crew op déze dag) */}
      <KlantVereistenBlok variant="compact" sx={{ mt: -1, mb: 1.5 }}
        projectId={(draaidag.fields?.Project || [])[0]}
        nummer={[].concat(draaidag.fields?.['Projectnummer (from Project)'] || [])[0]}
        datum={draaidag.fields?.StartDatum || null} />

      {pl && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>PL:</Typography>
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: stringToColor(pl) }}>
            {getInitials(pl)}
          </Avatar>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody }}>{pl}</Typography>
        </Box>
      )}

      {/* Callsheet & Quitclaim Status */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {callsheetNodig && (
          <Typography component="span" sx={stitchPill(
            aantalCallsheets > 0 ? '#166534' : callsheetNodig === 'Ja' ? '#92400e' : T.outline,
            aantalCallsheets > 0 ? '#dcfce7' : callsheetNodig === 'Ja' ? T.amberBg : T.surfaceLow
          )}>
            {'\u{1F4DE}'} Callsheet: {callsheetNodig}{aantalCallsheets > 0 ? ` \u2713 (${aantalCallsheets})` : ''}
          </Typography>
        )}
        {quitclaimsNodig && (
          <Typography component="span" sx={stitchPill(
            quitclaims.length > 0 ? '#166534' : quitclaimsNodig.includes('Ja') ? '#92400e' : T.outline,
            quitclaims.length > 0 ? '#dcfce7' : quitclaimsNodig.includes('Ja') ? T.amberBg : T.surfaceLow
          )}>
            {'\u{1F4CB}'} Quitclaims: {quitclaimsNodig}{quitclaims.length > 0 ? ` \u2713 (${quitclaims.length})` : ''}
          </Typography>
        )}
        {geenMontage && (
          <Typography component="span" sx={stitchPill('#1d4ed8', '#dbeafe')}>
            Geen montage nodig
          </Typography>
        )}
      </Box>

      {/* Crew */}
      {crew.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }} display="block" mb={1}>Crew</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {crew.map((person, idx) => {
              // Clean up name: remove (b), (g), etc.
              const cleanName = person.replace(/\s*\([^)]*\)\s*$/, '').trim();
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    bgcolor: T.surfaceLow,
                    borderRadius: '9999px',
                    pl: 0.5,
                    pr: 1.5,
                    py: 0.25,
                  }}
                >
                  <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem', bgcolor: stringToColor(cleanName) }}>
                    {getInitials(cleanName)}
                  </Avatar>
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, fontWeight: 500, fontSize: '0.75rem' }}>
                    {cleanName}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {rapporten.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {rapporten.map(r => <EindeDraaidagRapport key={r.id} rapport={r} />)}
        </Box>
      )}
    </Box>
  );
}

export default DraaidagCard;
